import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const UNIT_TYPES = [
  { value: 'Apt', label: 'Apt' },
  { value: 'Suite', label: 'Suite' },
  { value: 'Unit', label: 'Unit' },
  { value: 'Floor', label: 'Floor' },
  { value: 'Bldg', label: 'Bldg' },
  { value: '#', label: '#' },
];

interface Address2InputProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function Address2Input({
  value = '',
  onChange,
  placeholder = 'e.g., 123',
  disabled = false,
}: Address2InputProps) {
  const [unitType, setUnitType] = useState('');
  const [unitNumber, setUnitNumber] = useState('');

  // Parse initial value if provided
  useEffect(() => {
    if (value && !unitType && !unitNumber) {
      // Special handling for # pattern without space (e.g., "#2")
      const hashMatch = value.match(/^#(.+)$/);
      if (hashMatch) {
        setUnitType('#');
        setUnitNumber(hashMatch[1]!);
        return;
      }

      // Try to parse the value into type and number (space-separated)
      const parts = value.trim().split(' ');
      if (parts.length >= 2) {
        const possibleType = parts[0]!;
        const matchingType = UNIT_TYPES.find((t) => t.value === possibleType);
        if (matchingType) {
          setUnitType(possibleType);
          setUnitNumber(parts.slice(1).join(' '));
          return;
        }
      }
      // If we can't parse it, leave type empty and put everything in the number field
      setUnitNumber(value);
    }
  }, [value, unitType, unitNumber]);

  // Update parent when type or number changes
  const handleUpdate = (newType: string, newNumber: string) => {
    if (!newType && !newNumber) {
      onChange('');
    } else if (!newType) {
      onChange(newNumber);
    } else if (newType === '#') {
      // Special handling for # - no space
      onChange(`${newType}${newNumber}`.trim());
    } else {
      onChange(`${newType} ${newNumber}`.trim());
    }
  };

  const handleTypeChange = (newType: string) => {
    setUnitType(newType);
    handleUpdate(newType, unitNumber);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value;
    setUnitNumber(newNumber);
    handleUpdate(unitType, newNumber);
  };

  return (
    <div className="flex gap-2">
      <Select value={unitType || undefined} onValueChange={handleTypeChange} disabled={disabled}>
        <SelectTrigger className="w-[120px]">
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          {UNIT_TYPES.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={unitNumber}
        onChange={handleNumberChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1"
      />
    </div>
  );
}
